import type {
  SeasonalityKeyword,
  TrendKeyword,
  TrendSeriesField,
} from '../models/trendSeasonality.types'

export type TrendPivotField = Exclude<TrendSeriesField, 'seasonal'>
export type HeatTone = 'neutral' | 'negative' | 'positive'

export interface HeatCell {
  value: number | null
  tone: HeatTone
  /** Legacy ramp opacity expressed as a token mix percentage by the render layer. */
  intensity: number
}

export interface TrendPivotColumn {
  /** Stable, locale-independent field used by DataTable and sorting. */
  field: string
  month: number
  year: number | null
}

export type TrendPivotRow = Record<string, unknown> & {
  query: string
  cells: Record<string, HeatCell>
}

export interface TrendPivot {
  columns: TrendPivotColumn[]
  rows: TrendPivotRow[]
  total: TrendPivotRow
}

export function calcMedian(values: readonly number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((left, right) => left - right)
  const middle = Math.floor(sorted.length / 2)
  const current = sorted[middle]
  if (current === undefined) return null
  if (sorted.length % 2 === 1) return current
  const previous = sorted[middle - 1]
  return previous === undefined ? current : (previous + current) / 2
}

/** Exact legacy split: low half fades red 1→.1; high half grows green .1→1. */
export function heatCell(
  value: number | null,
  min: number,
  max: number,
  median: number | null,
): HeatCell {
  if (value === null || !Number.isFinite(value) || median === null || min === max) {
    return {
      value: value !== null && Number.isFinite(value) ? value : null,
      tone: 'neutral',
      intensity: 0,
    }
  }
  if (value <= median) {
    const range = median - min
    const intensity = range === 0 ? 0.1 : Math.max(0.1, 1 - ((value - min) / range) * 0.9)
    return { value, tone: 'negative', intensity }
  }
  const range = max - median
  const intensity = range === 0 ? 0.1 : Math.min(1, 0.1 + ((value - median) / range) * 0.9)
  return { value, tone: 'positive', intensity }
}

export function buildSeasonalPivot(keywords: readonly SeasonalityKeyword[]): TrendPivot {
  const columns: TrendPivotColumn[] = Array.from({ length: 12 }, (_, index) => ({
    field: `month-${String(index + 1).padStart(2, '0')}`,
    month: index + 1,
    year: null,
  }))
  return buildPivot(
    keywords,
    columns,
    (keyword, column) =>
      keyword.data.find((point) => point.month === column.month)?.seasonal ?? null,
  )
}

export function buildRollingPivot(
  keywords: readonly TrendKeyword[],
  field: TrendPivotField,
  now: Date = new Date(),
): TrendPivot {
  const latest = latestObservedMonth(keywords) ?? addUtcMonths(toUtcMonth(now), -1)
  const columns = Array.from({ length: 14 }, (_, index) => {
    const date = addUtcMonths(latest, index - 11)
    const year = date.getUTCFullYear()
    const month = date.getUTCMonth() + 1
    return { field: `${year}-${String(month).padStart(2, '0')}`, month, year }
  })
  return buildPivot(keywords, columns, (keyword, column) => {
    const point = keyword.data.find((item) => item.month === column.field)
    return point?.[field] ?? null
  })
}

function buildPivot<Keyword extends { query: string }>(
  keywords: readonly Keyword[],
  columns: readonly TrendPivotColumn[],
  valueAt: (keyword: Keyword, column: TrendPivotColumn) => number | null,
): TrendPivot {
  const rows = keywords.map<TrendPivotRow>((keyword) => {
    const values = columns.map((column) => valueAt(keyword, column))
    const numeric = values.filter(
      (value): value is number => value !== null && Number.isFinite(value),
    )
    const min = numeric.length > 0 ? Math.min(...numeric) : 0
    const max = numeric.length > 0 ? Math.max(...numeric) : 0
    const median = calcMedian(numeric)
    const cells = Object.fromEntries(
      columns.map((column, index) => [
        column.field,
        heatCell(values[index] ?? null, min, max, median),
      ]),
    )
    return Object.assign(
      { query: keyword.query, cells },
      Object.fromEntries(
        columns.map((column) => [column.field, cells[column.field]?.value ?? null]),
      ),
    )
  })
  const totalCells = Object.fromEntries(
    columns.map((column) => {
      const value = rows.reduce((sum, row) => {
        const cell = row.cells[column.field]
        return sum + (cell?.value ?? 0)
      }, 0)
      return [column.field, { value, tone: 'neutral', intensity: 0 } satisfies HeatCell]
    }),
  )
  const total = Object.assign(
    { query: 'total', cells: totalCells },
    Object.fromEntries(
      columns.map((column) => [column.field, totalCells[column.field]?.value ?? 0]),
    ),
  )
  return { columns: [...columns], rows, total }
}

function latestObservedMonth(keywords: readonly TrendKeyword[]): Date | null {
  let latest: Date | null = null
  for (const keyword of keywords) {
    for (const point of keyword.data) {
      if (point.is_forecasted) continue
      const parsed = parseYearMonth(point.month)
      if (parsed && (!latest || parsed > latest)) latest = parsed
    }
  }
  return latest
}

function parseYearMonth(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})$/u.exec(value)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  if (month < 1 || month > 12) return null
  return new Date(Date.UTC(year, month - 1, 1))
}

function toUtcMonth(value: Date): Date {
  return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), 1))
}

function addUtcMonths(value: Date, offset: number): Date {
  return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth() + offset, 1))
}
